/**
 * Spec 085 — dealer credit, admin side. Shapes mirror orders `DealerCreditViews` / `DealerCreditRequests`
 * (the backend is the contract). Money is a 2-dp string ("9500.00"); inputs accept a decimal string.
 */

export type DealerStatus = 'DRAFT' | 'ACTIVE' | 'SUSPENDED';
export const DEALER_STATUSES: DealerStatus[] = ['DRAFT', 'ACTIVE', 'SUSPENDED'];

export interface DealerContact {
  name: string;
  phone: string;
  email: string | null;
}

/** BR-36 — discount vs the lowest live commission; `lowestCommissionPct` absent when unknown. */
export interface BreakEven {
  lowestCommissionPct?: string;
  marginPer1000Aed?: string;
  warning: boolean;
}

export interface Dealer {
  id: string;
  legalName: string;
  tradeName: string;
  trn?: string;
  billingEmail: string;
  contact: DealerContact;
  discountPct: string;
  paymentTermsDays?: number;
  status: DealerStatus;
  agreementOnFile: boolean;
  agreementSignedAt?: string;
  createdAt: string;
  updatedAt?: string;
  /** Only on create and on a PATCH that sent `discountPct`. */
  breakEven?: BreakEven;
}

export interface ContactInput {
  name: string;
  phone: string;
  email?: string | null;
}

export interface CreateDealerRequest {
  legalName: string;
  tradeName: string;
  trn?: string | null;
  billingEmail: string;
  contact: ContactInput;
  discountPct: string;
  paymentTermsDays?: number | null;
}

/** PATCH: an absent field is unchanged. */
export type UpdateDealerRequest = Partial<CreateDealerRequest>;

export type DealerUserRole = 'OWNER' | 'STAFF';
export type DealerUserStatus = 'ACTIVE' | 'DISABLED';

export interface DealerUser {
  id: string;
  subjectId: string | null;
  name: string;
  phone: string;
  email: string;
  /** Sign-in username (BR-2). Null for dealer users created before usernames: they sign in with the email. */
  username: string | null;
  role: DealerUserRole;
  canCommitSpend: boolean;
  status: DealerUserStatus;
  agreementAcceptedAt: string | null;
  createdAt: string;
}

export interface CreateDealerUserRequest {
  name: string;
  phone: string;
  email: string;
  /** Lowercase; garage-partner username rules (checked live via /api/auth/username/availability). */
  username: string;
  role: DealerUserRole;
  canCommitSpend: boolean;
}

export interface UpdateDealerUserRequest {
  role?: DealerUserRole;
  canCommitSpend?: boolean;
  status?: DealerUserStatus;
}

export interface Showroom {
  id: string;
  dealerId: string;
  dealerTradeName?: string;
  label: string;
  street?: string;
  city?: string;
  area?: string;
  emirate?: string;
  building?: string;
  latitude?: number;
  longitude?: number;
  instructions?: string;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface ShowroomRequest {
  label: string;
  street?: string | null;
  city?: string | null;
  area?: string | null;
  emirate?: string | null;
  building?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  instructions?: string | null;
}

export type CreditRequestKind = 'PER_BUYER' | 'PACK';
export type CreditRequestStatus = 'PROFORMA_ISSUED' | 'AWAITING_PAYMENT' | 'PAID' | 'REVOKED';
export const CREDIT_REQUEST_STATUSES: CreditRequestStatus[] = ['PROFORMA_ISSUED', 'AWAITING_PAYMENT', 'PAID', 'REVOKED'];

export interface CreditRequest {
  id: string;
  dealerId: string;
  kind: CreditRequestKind;
  packId?: string;
  buyerCreditId?: string;
  number?: string;
  faceValueAed: string;
  discountPct: string;
  payableAed: string;
  vatAed: string;
  /** What Mark paid must receive: payable + VAT (BR-14). */
  totalDueAed: string;
  status: CreditRequestStatus;
  acceptedAt?: string;
  paidAt?: string;
  bankReference?: string;
  valueDate?: string;
  invoiceNumber?: string;
  revokedAt?: string;
  revokeReason?: string;
  note?: string;
  createdAt: string;
  proformaUrl?: string;
}

export interface MarkPaidRequest {
  bankReference: string;
  receivedAmountAed: string;
  valueDate: string;
}

export type BuyerCreditStatus = 'PENDING' | 'LIVE' | 'EXPIRED' | 'REVOKED';
export const BUYER_CREDIT_STATUSES: BuyerCreditStatus[] = ['PENDING', 'LIVE', 'EXPIRED', 'REVOKED'];

export interface BuyerCredit {
  id: string;
  dealerId: string;
  requestId?: string;
  customerId?: string;
  hasAccount: boolean;
  buyerName?: string;
  buyerPhone?: string;
  faceValueAed: string;
  usedAed: string;
  reservedAed: string;
  remainingAed: string;
  expiredAed: string;
  revokedAed: string;
  status: BuyerCreditStatus;
  liveAt?: string;
  expiresAt?: string;
  note?: string;
  createdAt: string;
}

export interface DealerLot {
  id: string;
  source: 'PACK' | 'RETURN' | 'REFUND';
  requestId: string | null;
  faceValueAed: string;
  remainingAed: string;
  status: 'ACTIVE' | 'EXHAUSTED' | 'EXPIRED';
  expiresAt: string | null;
  createdAt: string;
}

/** Revoke-after-paid (super admin): X defaults to the unreserved remaining, C to X × (1 − discount); C ≤ X. */
export interface RevokeAfterPaidRequest {
  reason: string;
  amountAed?: string;
  refundToDealerAed?: string;
}

export interface DealerRefund {
  id: string;
  dealerId: string;
  creditId?: string;
  lotId?: string;
  revokedAed: string;
  amountAed: string;
  discountReversedAed: string;
  vatAed: string;
  owedAed: string;
  status: 'DUE' | 'PAID';
  creditNoteNumber?: string;
  reason?: string;
  bankReference?: string;
  valueDate?: string;
  paidAt?: string;
  createdAt: string;
}

export interface MarkPaidResult {
  request: CreditRequest;
  credit?: BuyerCredit;
  lot?: DealerLot;
  invoiceNumber?: string;
}

export type DealerDocumentType = 'PROFORMA' | 'TAX_INVOICE' | 'CREDIT_NOTE';

export interface DealerDocument {
  id: string;
  type: DealerDocumentType;
  number?: string;
  requestId?: string;
  dealerRefundId?: string;
  amountAed?: string;
  vatAed?: string;
  status: 'PENDING' | 'RENDERED' | 'SENT' | 'VOID';
  createdAt: string;
  downloadUrl?: string;
  /** The email's true state; absent until the first request goes out, or while a resend is queued. */
  deliveryStatus?: DealerDocumentDelivery;
}

export type DealerDocumentDelivery = 'REQUESTED' | 'SENT' | 'DELIVERED' | 'FAILED_RETRYING' | 'FAILED';

export type PackStatus = 'ACTIVE' | 'INACTIVE';

export interface CreditPack {
  id: string;
  name: string;
  faceValueAed: string;
  discountPct: string;
  status: PackStatus;
}

export interface PackRequest {
  name?: string;
  faceValueAed?: string;
  discountPct?: string;
  status?: PackStatus;
}

/** BR-32 — one dealer-user action. */
export interface DealerAction {
  id: string;
  subjectId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
}

export interface DealerPage<T> {
  content: T[];
  nextCursor: string | null;
  hasMore: boolean;
  size?: number;
}

export interface UploadUrl {
  uploadUrl: string;
  storageKey: string;
}
